// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import L from 'leaflet';
import type { ViewContext } from '../viewContext';
import { loadGeo } from '../data';
import { OUTCOME_COLOUR, OUTCOME_LABEL, OUTCOME_ORDER, speciesInfo, stateName, stateColour } from '../domain';
import { stateStats } from '../analysis';
import { RAMP, rampColour, niceCeil } from '../components/charts';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatNumber, formatPercent, titleCase, formatIncidentDate } from '../utils/format';

type Mode = 'points' | 'count' | 'perMillion' | 'fatalPct';
const MODES: Record<Mode, { label: string; title: string; sub: string; fmt: (v: number) => string; unit: string }> = {
  points: {
    label: 'Every bite',
    title: 'Every geolocated incident',
    sub: 'One dot per recorded incident, coloured by outcome. Fatal bites cluster where large white and tiger sharks meet crowded surf. Click a dot for the full record.',
    fmt: (v) => formatNumber(v),
    unit: '',
  },
  count: {
    label: 'By state',
    title: 'Total incidents by state',
    sub: 'Raw counts — a map of where Australians swim and surf as much as where sharks are. Click a state to open it.',
    fmt: (v) => formatNumber(v),
    unit: 'incidents',
  },
  perMillion: {
    label: 'Per million residents',
    title: 'Incidents per million residents',
    sub: 'Adjusting for population flips the picture: sparsely settled tropical coasts carry a far higher rate per resident than the big south-eastern states. A rough exposure proxy, not per-swimmer.',
    fmt: (v) => v.toFixed(0),
    unit: 'per million',
  },
  fatalPct: {
    label: 'Fatal share',
    title: 'Share of incidents that were fatal',
    sub: 'The proportion of each state\'s incidents that proved fatal. Higher where tiger and white sharks dominate and help is further away.',
    fmt: (v) => `${v.toFixed(0)}%`,
    unit: '% fatal',
  },
};

const MAINLAND = L.latLngBounds([-44.5, 112], [-9.5, 154.5]);

export function renderMap(root: HTMLElement, ctx: ViewContext): void {
  let mode: Mode = (localStorage.getItem('sharks.map.mode') as Mode) || 'points';
  if (!MODES[mode]) mode = 'points';
  const states = stateStats(ctx.data.incidents);
  const byState = new Map(states.map((s) => [s.st, s]));

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">The map of Australian shark incidents</h1>
      <p class="view-sub">
        Every geolocated ${glossaryTerm('incident', 'incident')} since ${ctx.data.meta.dataFrom}. Switch between individual
        bites and state summaries. Hover for detail; click a dot for the full record or a state for its overview.
      </p>
    </div>
    <div class="controls">
      <span class="field-label">Show</span>
      <div class="seg" role="group" data-role="mode">
        ${(Object.keys(MODES) as Mode[]).map((m) => `<button class="seg-btn" data-mode="${m}" aria-pressed="${m === mode}">${MODES[m].label}</button>`).join('')}
      </div>
    </div>
    <div class="panel">
      <div class="panel-title" data-role="title"></div>
      <div class="panel-sub" data-role="sub"></div>
      <div class="map-frame"><div class="map-canvas"></div></div>
      <div class="map-legend" data-role="legend"></div>
    </div>
  `;

  const canvas = root.querySelector('.map-canvas') as HTMLElement;
  const titleEl = root.querySelector('[data-role="title"]') as HTMLElement;
  const subEl = root.querySelector('[data-role="sub"]') as HTMLElement;
  const legendEl = root.querySelector('[data-role="legend"]') as HTMLElement;

  const map = L.map(canvas, { minZoom: 3, maxZoom: 11, scrollWheelZoom: false, preferCanvas: true });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO · Data: ASID (Taronga/Flinders/NSW DPI) · Boundaries: ABS ASGS 2021',
    subdomains: 'abcd',
    minZoom: 3,
    maxZoom: 11,
  }).addTo(map);
  map.fitBounds(MAINLAND, { padding: [6, 6] });

  const stateValue = (st: string, m: Mode): number | null => {
    const s = byState.get(st);
    if (!s) return null;
    if (m === 'count') return s.total;
    if (m === 'perMillion') return s.perMillion;
    if (m === 'fatalPct') return s.fatalPct;
    return null;
  };
  const stopsFor = (m: Mode): number[] => {
    const vals = states.map((s) => stateValue(s.st, m)).filter((v): v is number => v != null);
    const max = niceCeil(Math.max(...vals, 1));
    return [1, 2, 3, 4, 5, 6].map((i) => (max / 6) * i);
  };

  let polyLayer: L.GeoJSON | null = null;
  const markerLayer = L.layerGroup();
  const geocoded = ctx.data.incidents.filter((it) => it.lat != null && it.lng != null);
  for (const it of geocoded) {
    const marker = L.circleMarker([it.lat as number, it.lng as number], {
      radius: it.out === 'fatal' ? 5 : 4,
      fillColor: OUTCOME_COLOUR[it.out],
      color: '#ffffff',
      weight: 0.6,
      fillOpacity: 0.78,
    });
    marker.bindTooltip(
      `<strong>${escapeHtml(it.loc ? titleCase(it.loc) : 'Unknown')}</strong><br>` +
        `${escapeHtml(formatIncidentDate(it.y, it.m, null))} · ${escapeHtml(speciesInfo(it.sp).label)}<br>` +
        `<span style="color:${OUTCOME_COLOUR[it.out]};font-weight:600">${OUTCOME_LABEL[it.out]}</span>`,
      { direction: 'top', className: 'map-tip', opacity: 0.97 },
    );
    marker.on('click', () => ctx.openIncident(it.id));
    marker.on('mouseover', () => marker.setStyle({ weight: 2, radius: it.out === 'fatal' ? 7 : 6 }));
    marker.on('mouseout', () => marker.setStyle({ weight: 0.6, radius: it.out === 'fatal' ? 5 : 4 }));
    markerLayer.addLayer(marker);
  }

  const renderLegend = () => {
    if (mode === 'points') {
      legendEl.innerHTML =
        `<span class="field-label">Outcome</span>` +
        OUTCOME_ORDER.map((o) => `<span class="legend-item" style="cursor:default"><span class="legend-swatch round" style="background:${OUTCOME_COLOUR[o]}"></span>${OUTCOME_LABEL[o]}</span>`).join('') +
        `<span class="legend-item" style="cursor:default;margin-left:auto;color:var(--text-tertiary)">${formatNumber(geocoded.length)} dots</span>`;
      return;
    }
    const cfg = MODES[mode];
    const stops = stopsFor(mode);
    legendEl.innerHTML =
      `<span class="field-label">${escapeHtml(cfg.unit)}</span>` +
      `<span class="scale-bar">${RAMP.map((c) => `<span class="scale-swatch" style="background:${c}"></span>`).join('')}</span>` +
      `<span style="font-family:var(--font-mono);font-size:var(--font-size-xs)">${[0, ...stops].map((v) => cfg.fmt(v)).join(' · ')}</span>`;
  };

  const styleState = (code: string) => {
    if (mode === 'points') return { fillOpacity: 0, opacity: 0, color: '#fff', weight: 0, fillColor: '#fff' };
    const v = stateValue(code, mode);
    return { fillColor: rampColour(v, stopsFor(mode)), fillOpacity: 0.82, color: '#ffffff', weight: 1 };
  };

  const paint = () => {
    const cfg = MODES[mode];
    titleEl.textContent = cfg.title;
    subEl.textContent = cfg.sub;
    renderLegend();
    root.querySelectorAll('[data-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-mode') === mode)));
    if (mode === 'points') {
      if (!map.hasLayer(markerLayer)) markerLayer.addTo(map);
    } else if (map.hasLayer(markerLayer)) {
      map.removeLayer(markerLayer);
    }
    if (polyLayer) {
      polyLayer.setStyle((f: any) => styleState(f.properties.code));
      polyLayer.eachLayer((lyr: any) => {
        const code = lyr.feature.properties.code;
        lyr.setTooltipContent(stateTip(code));
      });
    }
  };

  const stateTip = (code: string): string => {
    const s = byState.get(code);
    if (!s) return `<strong>${escapeHtml(stateName(code))}</strong><br><span style="color:var(--text-tertiary)">No incidents</span>`;
    return (
      `<strong>${escapeHtml(stateName(code))}</strong><br>` +
      `${formatNumber(s.total)} incidents · ${formatNumber(s.fatal)} fatal<br>` +
      `<span style="color:var(--text-secondary)">${formatPercent(s.fatalPct, 0)} fatal · ${s.perMillion != null ? `${s.perMillion.toFixed(0)}/million` : '—'}</span>`
    );
  };

  root.querySelector('[data-role="mode"]')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-mode]');
    if (!btn) return;
    mode = btn.getAttribute('data-mode') as Mode;
    localStorage.setItem('sharks.map.mode', mode);
    paint();
  });

  loadGeo()
    .then((geo) => {
      polyLayer = L.geoJSON(geo as any, {
        style: (f: any) => styleState(f.properties.code),
        onEachFeature: (f: any, lyr: L.Layer) => {
          const code = f.properties.code;
          lyr.bindTooltip(stateTip(code), { sticky: true, className: 'map-tip' });
          lyr.on({
            mouseover: () => {
              if (mode !== 'points') (lyr as L.Path).setStyle({ weight: 2.4, color: '#0e2233' });
            },
            mouseout: () => polyLayer?.resetStyle(lyr as L.Path),
            click: () => {
              if (mode !== 'points') ctx.openState(code);
            },
          });
        },
      }).addTo(map);
      markerLayer.addTo(map);
      requestAnimationFrame(() => {
        map.invalidateSize();
        map.fitBounds(MAINLAND, { padding: [6, 6] });
      });
      paint();
    })
    .catch(() => {
      canvas.innerHTML = '<div class="error-box" style="margin:var(--space-lg)">Could not load the state boundaries.</div>';
    });

  paint();

  // State quick-zoom.
  const zooms: Record<string, [number, number, number]> = {
    NSW: [-32.5, 152, 6],
    VIC: [-38, 145, 6],
    QLD: [-20, 147, 5],
    SA: [-34, 137, 6],
    WA: [-27, 118, 5],
    TAS: [-42, 147, 7],
    NT: [-13, 132, 6],
  };
  const zoomBar = document.createElement('div');
  zoomBar.className = 'controls';
  zoomBar.style.marginTop = 'var(--space-md)';
  zoomBar.innerHTML =
    '<span class="field-label">Jump to</span>' +
    Object.keys(zooms).map((s) => `<button class="chip" data-zoom="${s}" style="color:${stateColour(s)};font-weight:700">${s}</button>`).join('') +
    '<button class="chip" data-zoom="ALL">All of Australia</button>';
  (root.querySelector('.panel') as HTMLElement).appendChild(zoomBar);
  zoomBar.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-zoom]');
    if (!btn) return;
    const s = btn.getAttribute('data-zoom') as string;
    if (s === 'ALL') {
      map.fitBounds(MAINLAND, { padding: [6, 6] });
      return;
    }
    const [lat, lng, z] = zooms[s];
    map.setView([lat, lng], z);
  });
}
